import { useContext, useEffect, useState } from 'react';
import { appActions, AppContext } from 'context';
import { classnames } from 'helpers/utils';
import { Line } from 'common/line';
import { Button } from 'common/button';
import { Footer, FooterType } from 'common/footer';
import { goToPage, RouteName } from 'routes';
import globalStyles from 'assets/stylesheets/global-styles.module.scss';
import { Spinner } from 'common/spinner';
import { UserController } from 'networking/controllers/user-controller';
import { useParams } from 'react-router-dom';
import { LocalStorageApi } from 'helpers/local-storage';
import { LegacyVideoSection } from 'common/legacy-video-section/legacy-video-section';
import { LegacyVideoModal } from 'common/legacy-video-modal/legacy-video-modal';
import successCheck from 'assets/lottie/successCheck.json';
import Lottie from 'lottie-react';
import { Breakpoints } from 'common/enums';
import { useMediaQuery } from 'hooks/use-media-query';
import { history } from '../../routes/routes';
import styles from './email-verifying.module.scss';

type ParamType = {
  emailVerificationToken: string;
};

enum Viewstatus {
  Verifying = 'verifying',
  Succeeded = 'succeeded',
  Failed = 'failed',
}

const EmailVerifying = () => {
  const { state, dispatch } = useContext(AppContext);

  const { emailVerificationToken } = useParams<ParamType>();
  const email = new URLSearchParams(window.location.search).get('email');

  const [viewStatus, setViewStatus] = useState(Viewstatus.Verifying);
  const [fetching, setFetching] = useState(true);
  const [showVideo, setShowVideo] = useState(false);

  const previousUrl = LocalStorageApi.get('previous-url');
  const isMobileOrTablet = useMediaQuery(`(max-width: ${Breakpoints.md}px)`);

  const verifyUser = async () => {
    try {
      await UserController.verifyUserEmail({
        emailVerificationToken,
        email: email!,
      });

      const data = await UserController.me();
      dispatch({ type: appActions.USER_LOGGED, data });

      setViewStatus(Viewstatus.Succeeded);
    } catch (err: any) {
      const data = await UserController.me();
      dispatch({ type: appActions.USER_LOGGED, data });

      if (data.user.verified) {
        goToPage(RouteName.Home);
      } else {
        setViewStatus(Viewstatus.Failed);
      }
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    if (!emailVerificationToken || !email) {
      goToPage(RouteName.NotFoundRedirect);
    } else {
      verifyUser();
    }
  }, []);

  const handleAction = () => {
    if (viewStatus === Viewstatus.Failed) {
      setViewStatus(Viewstatus.Verifying);
      setFetching(true);
      verifyUser();
    }

    if (viewStatus === Viewstatus.Succeeded) {
      if (state.data.user.id) {
        if (previousUrl) {
          history.push(previousUrl);
          LocalStorageApi.set('previous-url', '');
        } else {
          goToPage(RouteName.Home);
        }
      } else {
        goToPage(RouteName.SignIn);
      }
    }
  };

  const showVeryfing = () => (
    <>
      <p className={classnames('text__title1__gray800', styles.titleMargin)}>
        Verifying your email...
      </p>
      <div className={styles.spinnerContent}>
        <Spinner className={globalStyles.mediumSpinner} />
      </div>
    </>
  );

  const showSucceded = () => (
    <div>
      <Lottie
        animationData={successCheck}
        className={styles.lottie}
        loop={false}
      />
      <div className={styles.titleSucceded}>
        <p className={classnames('text__title1__gray800', styles.titleMargin)}>
          Your email has been verified!
        </p>
        <p className="text__body__regular__medium__gray800">
          You can now continue to Generational Story.
        </p>
      </div>
    </div>
  );

  const showFailed = () => (
    <div>
      <div className={classnames('text__title1__gray800', styles.titleMargin)}>
        Something went wrong verifying your email.
      </div>
      <div
        className={classnames(
          'text__body__regular__medium__gray700',
          styles.titleMargin,
        )}
      >
        Please click the button below to try again. If that doesn’t work, click
        the link on your email again.
      </div>
    </div>
  );

  const showRightSection = () => (
    <div className={globalStyles.signBoxContent}>
      <div className={styles.elements}>
        {viewStatus === Viewstatus.Verifying && showVeryfing()}
        {viewStatus === Viewstatus.Succeeded && showSucceded()}
        {viewStatus === Viewstatus.Failed && showFailed()}

        {viewStatus === Viewstatus.Verifying && (
          <Line className={styles.line} />
        )}

        {viewStatus !== Viewstatus.Verifying && (
          <Button disabled={fetching} onClick={handleAction}>
            {viewStatus === Viewstatus.Failed ? 'Verify Email' : 'Continue'}
          </Button>
        )}
      </div>
    </div>
  );

  const showContent = () => (
    <div className={styles.container}>
      {showVideo && <LegacyVideoModal onClose={() => setShowVideo(false)} />}
      {!isMobileOrTablet && (
        <LegacyVideoSection showVideo={() => setShowVideo(true)} />
      )}
      <div className={styles.contentRight}>
        <div className={styles.contentFormWrapper}>{showRightSection()}</div>
        <div className={styles.contentFooter}>
          <Footer footerType={FooterType.NoSignedUser} />
        </div>
      </div>
    </div>
  );

  return <>{showContent()}</>;
};

export { EmailVerifying };
